import type { Id } from '@minty/shared/ids';

const DEFAULT_CONTENT_TYPE = 'application/octet-stream';

/** A stored upload, and the server's receipt that this user uploaded it. The
 *  send hands both back, so an id alone attaches nothing. */
export type UploadedAttachment = { fileId: Id<'files'>; receipt: string };

/**
 * Upload URLs arrive in file order, one per file, because the same mutation
 * that validated the budget issued them. There is nothing here to re-validate —
 * a failure is a failure, and the send simply never happens. Each URL is a
 * signed `POST` that stores the bytes and answers `{ fileId, receipt }`.
 */
export async function uploadAttachments(
  files: Array<File>,
  uploadUrls: Array<string>,
  request: typeof fetch = fetch,
): Promise<Array<UploadedAttachment>> {
  return await Promise.all(
    files.map(async (file, index) => {
      const uploadUrl = uploadUrls[index];

      /** One url is requested per file, so a gap means the two lists came from
       *  different places — worth failing on rather than posting to nowhere. */
      if (uploadUrl === undefined) {
        throw new Error('No upload url for attachment');
      }

      const response = await request(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type || DEFAULT_CONTENT_TYPE },
        body: file,
      });

      if (!response.ok) {
        throw new Error(
          `Attachment upload failed with status ${response.status}.`,
        );
      }

      const { fileId, receipt } = (await response.json()) as {
        fileId: Id<'files'>;
        receipt?: string;
      };

      if (!receipt) {
        throw new Error('Attachment upload answered without a receipt.');
      }

      return { fileId, receipt };
    }),
  );
}
